import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LeadMagnetForm } from "@/components/marketing/LeadMagnetForm";
import {
  CheckCircle2,
  AlertTriangle,
  Eye,
  Target,
  TrendingUp,
  Users,
  Repeat,
  BookOpen,
  Calendar,
  ArrowRight,
} from "lucide-react";

/** Single source of truth for FAQ Q/A — used both for rendered HTML and FAQPage JSON-LD so they
 * never drift apart. Keeping answers ~2-3 sentences and definitive helps AI answer engines extract
 * a clean snippet. */
const FAQ_ITEMS: Array<{ q: string; a: string }> = [
  {
    q: "What does a SaaS positioning advisor do?",
    a: "A SaaS positioning advisor helps founders make their product clear and obvious to a specific buyer. The work shows up as a sharper Ideal Customer Profile, a positioning statement your team can actually use, and messaging that lifts conversion on landing pages, demos, and sales calls. The goal is fewer 'interesting' leads and more 'this is exactly what I need' buyers.",
  },
  {
    q: "Why isn't my SaaS getting new customers after raising a Seed round?",
    a: "In most Seed-stage SaaS companies, the bottleneck is not sales effort. It is that the positioning is unclear and the target customer does not know you exist. Fixing positioning is what makes outbound, content, and paid acquisition convert at a reasonable cost; without it, every channel underperforms at the same time.",
  },
  {
    q: "How long does fixing SaaS positioning take?",
    a: "For most Seed to pre-Series-A SaaS companies, a focused positioning pass takes two to six weeks. The first measurable lift on landing-page conversion or demo-to-close usually appears within 30 to 60 days of rolling the new positioning out to every customer-facing surface.",
  },
  {
    q: "Should a Seed-stage SaaS founder hire an advisor?",
    a: "An advisor pays for themselves the first time they prevent a wrong hire, a wasted quarter on the wrong channel, or a positioning mistake that compounds for a year. For Seed to pre-Series-A founders without a former CMO on the cap table, a part-time advisor is the highest-leverage way to import that experience without the burn.",
  },
  {
    q: "What is the difference between positioning and messaging?",
    a: "Positioning is the strategic choice of who you serve, what category you compete in, and why you are the best in that category. Messaging is how you say it on a landing page, in a cold email, or on stage. Messaging without positioning is just copywriting—you write fast and convert poorly.",
  },
  {
    q: "Do you only work with B2B SaaS founders?",
    a: "The advisory focus is B2B SaaS companies between Seed and Series A, with a live product and at least a handful of paying customers. The same principles apply to B2C SaaS and platform companies, but the playbook is sharpest for B2B SaaS where positioning has the largest revenue impact.",
  },
  {
    q: "Do you work with founders in Canada and the United States?",
    a: "Yes. The focus is Seed to pre-Series-A SaaS founders. Engagements run with weekly working sessions & timezone differences across Pacific, Mountain, Central, and Eastern are not a constraint.",
  },
  {
    q: "How do you measure whether new positioning is working?",
    a: "Three signals usually move first: landing-page conversion rate, demo-to-close rate, and the language inbound prospects use to describe what you do. Pipeline math takes 60 to 90 days to recompose, but the leading indicators show up inside 30.",
  },
  {
    q: "How is this different from hiring a marketing agency?",
    a: "An agency executes campaigns. An advisor helps you decide what to execute. The work upstream of any agency—sharpening positioning, ICP, and the message hierarchy—is what determines whether agency dollars produce pipeline or noise.",
  },
];

/** All three structured-data blocks combined into one @graph so we render exactly one inline JSON-LD
 * script. Keep `Service.areaServed`, `audience`, and `Person.sameAs` in sync with About-section copy. */
const STRUCTURED_DATA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Person",
      "@id": "https://www.mattrebeiro.com/#matt",
      name: "Matt Rebeiro",
      jobTitle: "Strategic Advisor for SaaS Founders",
      url: "https://www.mattrebeiro.com",
      sameAs: [
        "https://www.linkedin.com/in/mattrebeiro/",
        "https://x.com/themattrebeiro",
        "https://www.techretriever.io",
      ],
    },
    {
      "@type": "Service",
      "@id": "https://www.mattrebeiro.com/saas-positioning-advisor#service",
      name: "SaaS Positioning Advisor for Seed to Pre-Series-A Founders",
      serviceType: "SaaS Positioning and Go-to-Market Advisory",
      provider: { "@id": "https://www.mattrebeiro.com/#matt" },
      areaServed: [
        { "@type": "Country", name: "Canada" },
        { "@type": "Country", name: "United States" },
      ],
      audience: {
        "@type": "BusinessAudience",
        audienceType: "B2B SaaS Founders and Co-Founders, Seed to Pre-Series A",
      },
      url: "https://www.mattrebeiro.com/saas-positioning-advisor",
      description:
        "Advisory for Seed to pre-Series-A SaaS founders in Canada and the United States who are not getting new customers. Fix unclear positioning and low awareness so pipeline and demo-to-close start moving.",
    },
    {
      "@type": "FAQPage",
      "@id": "https://www.mattrebeiro.com/saas-positioning-advisor#faq",
      mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ],
};

const PROBLEM_SYMPTOMS = [
  "Founders tell you the product is interesting and then never buy.",
  "Your demo gets compliments, but pipeline does not move.",
  "You raised on traction, yet the same channels suddenly stop scaling.",
  "Every other call asks, \"so who is this really for?\"",
  "Sales blames the leads. Marketing blames the reps. You suspect both are right.",
];

const POSITIONING_SYMPTOMS = [
  "Your homepage says what the product is, not what it changes.",
  "You compete against \"doing nothing in a spreadsheet\" but never name it.",
  "Sales calls keep getting derailed by use-case tangents.",
  "Your category looks crowded even though your wedge is unique.",
];

const AWARENESS_SYMPTOMS = [
  "You are invisible in the channels your ICP already uses.",
  "You don't appear in their consideration set—RFPs, lists, comparisons.",
  "Your content reaches founders like you, not buyers like them.",
  "You have no repeatable distribution motion outside warm intros.",
];

const OUTCOMES = [
  {
    icon: Users,
    title: "A clear ICP",
    body: "Sales and marketing point at the same buyer with the same words.",
  },
  {
    icon: TrendingUp,
    title: "Higher demo-to-close",
    body: "Demos shorten and conversion rises because prospects arrive pre-qualified.",
  },
  {
    icon: Target,
    title: "Qualified inbound",
    body: "Pipeline starts including buyers you never had to chase.",
  },
  {
    icon: Repeat,
    title: "A repeatable motion",
    body: "You can finally answer: where does the next $1M of ARR come from?",
  },
];

const PLAYBOOK_CHAPTERS = [
  "The 90-minute ICP refit (template included).",
  "A positioning statement framework that survives contact with sales.",
  "The Category vs. Wedge decision tree.",
  "12 distribution channels ranked by SaaS stage and ACV.",
  "The 30-60-90 day rollout plan across web, sales, and content.",
  "Seven positioning anti-patterns to avoid.",
];

export default function SaasPositioningAdvisor() {
  return (
    <>
      {/* Inline JSON-LD: Person + Service + FAQPage. dangerouslySetInnerHTML avoids React escaping
          the JSON; safe because STRUCTURED_DATA is a static object literal we control. */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(STRUCTURED_DATA) }}
      />

      <Navbar />

      <main id="main-content" className="pt-20 min-h-screen">
        {/* HERO */}
        <section className="relative pt-12 sm:pt-16 pb-20 overflow-hidden" aria-label="Page introduction">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-primary/15 rounded-full blur-[120px] -z-10 mix-blend-screen" aria-hidden="true" />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
            <Badge
              variant="outline"
              className="border-primary/30 text-primary px-4 py-1.5 backdrop-blur-sm bg-primary/5 uppercase tracking-wider text-xs mb-6"
              data-testid="badge-audience"
            >
              For SaaS Founders &middot; Seed to Series A
            </Badge>
            <h1
              className="text-3xl sm:text-5xl md:text-6xl font-display font-bold leading-[1.1] tracking-tight mb-6"
              data-testid="text-hero-title"
            >
              Why your SaaS isn't getting new customers&mdash;and the positioning playbook that fixes it
            </h1>
            <p
              className="text-lg sm:text-xl text-foreground/70 max-w-3xl leading-relaxed mb-8"
              data-testid="text-hero-subtitle"
            >
              You closed your Seed. The product works. The demo is solid. But pipeline is empty. It's not a sales problem&mdash;your positioning is unclear and your ideal customer doesn't know you exist. The free playbook below shows you how Seed to pre-Series-A founders fix both.
            </p>
            <LeadMagnetForm
              source="positioning-playbook-hero"
              ctaLabel="Subscribe to our Tech Retriever newsletter to receive the Playbook"
              testIdPrefix="leadform-hero"
            />
          </div>
        </section>

        {/* SECTION 2: YOU'RE NOT ALONE */}
        <section className="py-20 bg-secondary/20 border-y border-white/5" aria-label="Founder symptoms">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
              <h2 className="text-primary font-medium tracking-wide uppercase text-sm">If this sounds familiar</h2>
            </div>
            <p className="text-2xl sm:text-3xl font-display font-bold mb-8 leading-tight">
              The problem isn't you. It's a pattern repeated by almost every Seed-stage SaaS founder.
            </p>
            <ul className="space-y-3" role="list" aria-label="Common symptoms">
              {PROBLEM_SYMPTOMS.map((item, i) => (
                <li
                  key={i}
                  role="listitem"
                  className="flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-card/50"
                  data-testid={`symptom-${i}`}
                >
                  <AlertTriangle size={16} className="text-primary mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-sm text-foreground/80">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* SECTION 3: TWO ROOT CAUSES */}
        <section className="py-20" aria-label="Root cause analysis">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
              <h2 className="text-primary font-medium tracking-wide uppercase text-sm">What's actually happening</h2>
            </div>
            <p className="text-2xl sm:text-3xl font-display font-bold mb-10 leading-tight">
              Two root causes, not one. Both are fixable.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-white/10 p-6 md:p-8" data-testid="card-cause-positioning">
                <div className="flex items-center gap-3 mb-4">
                  <Target size={20} className="text-primary" aria-hidden="true" />
                  <h3 className="text-xl font-display font-bold">1. Your positioning is unclear</h3>
                </div>
                <p className="text-sm text-foreground/70 mb-4">
                  Customers can't tell who you're for and what you actually do.
                </p>
                <ul className="space-y-2.5" role="list">
                  {POSITIONING_SYMPTOMS.map((item, i) => (
                    <li key={i} role="listitem" className="flex items-start gap-2 text-sm" data-testid={`positioning-symptom-${i}`}>
                      <span className="text-primary mt-1 shrink-0" aria-hidden="true">&bull;</span>
                      <span className="text-foreground/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="border-white/10 p-6 md:p-8" data-testid="card-cause-awareness">
                <div className="flex items-center gap-3 mb-4">
                  <Eye size={20} className="text-primary" aria-hidden="true" />
                  <h3 className="text-xl font-display font-bold">2. Your ICP doesn't know you exist</h3>
                </div>
                <p className="text-sm text-foreground/70 mb-4">
                  Even your best buyers haven't heard your name.
                </p>
                <ul className="space-y-2.5" role="list">
                  {AWARENESS_SYMPTOMS.map((item, i) => (
                    <li key={i} role="listitem" className="flex items-start gap-2 text-sm" data-testid={`awareness-symptom-${i}`}>
                      <span className="text-primary mt-1 shrink-0" aria-hidden="true">&bull;</span>
                      <span className="text-foreground/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* SECTION 4: WHAT CHANGES WHEN YOU FIX THIS */}
        <section className="py-20 bg-secondary/20 border-y border-white/5" aria-label="Outcomes">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
              <h2 className="text-primary font-medium tracking-wide uppercase text-sm">What changes when you fix this</h2>
            </div>
            <p className="text-2xl sm:text-3xl font-display font-bold mb-10 leading-tight">
              Pipeline starts behaving like the math you sold investors on.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {OUTCOMES.map((outcome, i) => {
                const Icon = outcome.icon;
                return (
                  <Card key={i} className="border-white/10 p-6" data-testid={`outcome-${i}`}>
                    <Icon size={20} className="text-primary mb-3" aria-hidden="true" />
                    <h3 className="font-semibold mb-2">{outcome.title}</h3>
                    <p className="text-sm text-foreground/70 leading-relaxed">{outcome.body}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTION 5: WHAT'S INSIDE THE PLAYBOOK */}
        <section className="py-20" aria-label="What's inside the playbook">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
              <h2 className="text-primary font-medium tracking-wide uppercase text-sm">The free playbook</h2>
            </div>
            <p className="text-2xl sm:text-3xl font-display font-bold mb-8 leading-tight">
              What's inside <em>The SaaS Positioning Playbook for Seed-to-Series-A Founders</em>.
            </p>
            <div className="grid md:grid-cols-2 gap-3 mb-10">
              {PLAYBOOK_CHAPTERS.map((chapter, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-card/50"
                  data-testid={`chapter-${i}`}
                >
                  <BookOpen size={16} className="text-primary mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-sm text-foreground/80">{chapter}</p>
                </div>
              ))}
            </div>
            <LeadMagnetForm
              source="positioning-playbook-mid"
              ctaLabel="Subscribe to our Tech Retriever newsletter to receive the Playbook"
              align="center"
              testIdPrefix="leadform-mid"
            />
          </div>
        </section>

        {/* SECTION 6: ABOUT MATT */}
        <section className="py-20 bg-secondary/20 border-y border-white/5" aria-label="About Matt Rebeiro">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-[200px,1fr] gap-8 items-start">
              <div>
                <img
                  src="/images/hero/matt-rebeiro-hero-400.avif"
                  alt="Matt Rebeiro, strategic advisor for SaaS founders"
                  width={200}
                  height={200}
                  className="rounded-2xl w-full object-cover aspect-square border border-white/10"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px w-8 bg-primary" aria-hidden="true" />
                  <h2 className="text-primary font-medium tracking-wide uppercase text-sm">Why I wrote this</h2>
                </div>
                <h3 className="text-2xl sm:text-3xl font-display font-bold mb-4">
                  Matt Rebeiro &mdash; Strategic Advisor for SaaS Founders
                </h3>
                {/* TODO: Matt to provide final bio paragraph + verifiable proof points (years, # of advisees, notable companies). */}
                <p className="text-foreground/80 leading-relaxed mb-4">
                  I've spent 15+ years advising senior leaders how to scale. The same pattern repeats: the product works, the team is good, but the pipeline is stuck. Almost always, the fix is upstream of sales.
                </p>
                <p className="text-foreground/80 leading-relaxed mb-6">
                  I publish{" "}
                  <a
                    href="https://www.techretriever.io/subscribe"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    The Tech Retriever
                  </a>
                  , a newsletter for founders who want clearer thinking, not just clearer features.
                </p>
                <ul className="space-y-2" role="list" aria-label="Credentials">
                  <li className="flex items-start gap-2 text-sm" role="listitem">
                    <CheckCircle2 size={14} className="text-primary mt-0.5 shrink-0" aria-hidden="true" />
                    {/* TODO: Replace placeholder with verified credential. */}
                    <span className="text-foreground/80">15+ years advising and operating with senior leaders</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm" role="listitem">
                    <CheckCircle2 size={14} className="text-primary mt-0.5 shrink-0" aria-hidden="true" />
                    {/* TODO: Replace with verified count once Matt confirms. */}
                    <span className="text-foreground/80">Helped SaaS companies sharpen positioning between Seed and Series A</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm" role="listitem">
                    <CheckCircle2 size={14} className="text-primary mt-0.5 shrink-0" aria-hidden="true" />
                    {/* TODO: Add notable publications, talks, or company logos for credibility. */}
                    <span className="text-foreground/80">Writes The Tech Retriever for B2B SaaS founders in Canada and the US</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: FAQ */}
        <section className="py-20" aria-label="Frequently asked questions">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
              <h2 className="text-primary font-medium tracking-wide uppercase text-sm">Founder questions, answered</h2>
            </div>
            <p className="text-2xl sm:text-3xl font-display font-bold mb-10 leading-tight">
              SaaS positioning &amp; advisory FAQ
            </p>
            <div className="space-y-3">
              {FAQ_ITEMS.map((item, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-white/10 bg-card/50 p-5 open:border-primary/30 open:bg-primary/5 transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none font-semibold text-foreground">
                    <span>{item.q}</span>
                    <ArrowRight
                      size={16}
                      className="text-primary mt-1 shrink-0 transition-transform group-open:rotate-90"
                      aria-hidden="true"
                    />
                  </summary>
                  <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 8: SECONDARY CTA — BOOK A CALL */}
        <section
          id="book"
          className="py-20 bg-secondary/20 border-y border-white/5"
          aria-label="Book a 15-minute positioning call"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <Badge
                variant="outline"
                className="border-primary/30 text-primary px-4 py-1.5 backdrop-blur-sm bg-primary/5 uppercase tracking-wider text-xs mb-6"
              >
                <Calendar size={14} className="mr-2" aria-hidden="true" />
                15-minute call
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4 leading-tight">
                Already past the playbook? Book a 15-minute positioning teardown.
              </h2>
              <p className="text-foreground/70 max-w-2xl mx-auto">
                Best for Seed to pre-Series-A SaaS founders who already know their positioning is the bottleneck and want a second pair of eyes before they rewrite the homepage and rebuild the sales pitch.
              </p>
            </div>
            <div className="rounded-xl border border-white/10 overflow-hidden bg-white" data-testid="booking-calendar-embed">
              <iframe
                src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ0eYB6IQ29sIw69N5Rszvrwm5yTQKoQ4stwUBUa0988tRwfmy-3Ehxq9LwgeMNjJMIiQqvBGBCj?gv=true"
                style={{ border: 0 }}
                width="100%"
                height="600"
                frameBorder="0"
                title="Book a 15-minute SaaS positioning call with Matt Rebeiro"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
